import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Paper,
  TextField,
  IconButton,
  CircularProgress,
  Checkbox,
  TableSortLabel,
  TablePagination,
  Box,
  Typography,
  Button,
  Chip,
  alpha,
  useTheme,
  Snackbar,
  Alert,
  Autocomplete,
  TextField as MuiTextField
} from "@mui/material";
import { useThemeMode } from "../contexts/ThemeContext";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import DeleteIcon from "@mui/icons-material/Delete";
import apiService from "../services/api";
import { keyframes } from "@mui/material/styles"; 
import SearchIcon from "@mui/icons-material/Search";
import CancelIcon from "@mui/icons-material/Cancel";


// Add this animation keyframe after imports
const pulseAnimation = keyframes`
  0% {
    opacity: 1;
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(211, 47, 47, 0.4);
  }
  50% {
    opacity: 0.9;
    transform: scale(1.02);
    box-shadow: 0 0 0 8px rgba(211, 47, 47, 0);
  }
  100% {
    opacity: 1;
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(211, 47, 47, 0);
  }
`;

const shakeAnimation = keyframes`
  0%, 100% { transform: translateX(0); }
  10%, 30%, 50%, 70%, 90% { transform: translateX(-2px); }
  20%, 40%, 60%, 80% { transform: translateX(2px); }
`;






interface DataverseTransaction {
  category: string | undefined;
  ppp_sf_transactionid: string;
  ppp_sequenceno: number;
  ppp_description: string;
  ppp_amountsigned: number | null | undefined;
  ppp_transactiondate: string;
  ppp_category?: string;
  isManual?: boolean;

  // Balance / net movement fields from backend
  balance?: number | null | undefined;
  ppp_balance?: number | null | undefined;
  ppp_runningbalance?: number | null | undefined;
  ppp_netmovement?: number | null | undefined;
}

interface Props {
  jobId: string;
  transactions: DataverseTransaction[];
  onSelectionChange?: (ids: string[]) => void;
  onDataChanged?: () => void;
}

type Order = "asc" | "desc";

const formatMoney = (value: number | null | undefined) => {
  if (value == null || Number.isNaN(value)) return "";
  return value.toLocaleString("en-AU", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

const toSafeNumber = (value: number | null | undefined) => {
  if (value == null || Number.isNaN(value)) return 0;
  return Number(value);
};

// Category options - Sorted Alphabetically
const CATEGORY_OPTIONS = [
  { value: '🔄 Account Transfer', label: '🔄 Account Transfer' },
  { value: '💳 Account Fees', label: '💳 Account / Banking Fees' },
  { value: '❄️ Air Conditioning', label: '❄️ Air Conditioning / HVAC' },
  { value: '🛩️ Airfare', label: '🛩️ Airfare / Flights' },
  { value: '🏠 Airbnb', label: '🏠 Airbnb / Vacation Rental' },
  { value: '💰 Alimony Received', label: '💰 Alimony Received' },
  { value: '🎡 Amusement Park', label: '🎡 Amusement Park' },
  { value: '📱 App Subscription', label: '📱 App / Software Subscription' },
  { value: '🛒 Appliances', label: '🛒 Appliances' },
  { value: '⌚ Apple Watch', label: '⌚ Apple Watch / Wearable' },
  { value: '🎨 Art Gallery', label: '🎨 Art Gallery / Museum' },
  { value: '💰 ATM Fee', label: '💰 ATM / Withdrawal Fee' },
  { value: '👶 Baby Supplies', label: '👶 Baby Supplies' },
  { value: '🔋 Battery', label: '🔋 Batteries / Chargers' },
  { value: '💊 Beauty', label: '💊 Beauty / Cosmetics' },
  { value: '🎁 Birthday', label: '🎁 Birthday Gift' },
  { value: '💵 Bonus', label: '💵 Bonus / Commission' },
  { value: '📚 Books', label: '📚 Books / Textbooks' },
  { value: '🏃‍♂️ Bowling', label: '🏃‍♂️ Bowling' },
  { value: '🥊 Boxing', label: '🥊 Boxing / Martial Arts' },
  { value: '🧸 Boys Clothing', label: '🧸 Boys Clothing' },
  { value: '🏢 Business Insurance', label: '🏢 Business Insurance' },
  { value: '💼 Business Meals', label: '💼 Business / Client Meals' },
  { value: '📞 Business Phone', label: '📞 Business Phone / Internet' },
  { value: '✈️ Business Travel', label: '✈️ Business Travel' },
  { value: '📷 Camera', label: '📷 Camera / Photography' },
  { value: '💰 Capital Gains', label: '💰 Capital Gains' },
  { value: '🚗 Car Insurance', label: '🚗 Car Insurance' },
  { value: '🚗 Car Loan', label: '🚗 Car Loan Repayment' },
  { value: '🔧 Car Maintenance', label: '🔧 Car Service / Repairs' },
  { value: '📋 Car Registration', label: '📋 Car Registration / Rego' },
  { value: '🚗 Car Rental', label: '🚗 Car Rental' },
  { value: '🚗 Car Wash', label: '🚗 Car Wash / Detailing' },
  { value: '🎁 Charity', label: '🎁 Charity Donation' },
  { value: '⚡ Charger', label: '⚡ Charger / Cables' },
  { value: '👶 Child Support Paid', label: '👶 Child Support Paid' },
  { value: '💰 Child Support Received', label: '💰 Child Support Received' },
  { value: '👶 Childcare', label: '👶 Childcare / Daycare' },
  { value: '🎄 Christmas', label: '🎄 Christmas / Holiday Gifts' },
  { value: '🏛️ Church', label: '🏛️ Church / Religious Donation' },
  { value: '👕 Clothing', label: '👕 Clothing / Apparel' },
  { value: '💻 Cloud Storage', label: '💻 Cloud Storage (iCloud/Google Drive)' },
  { value: '☕ Coffee Shops', label: '☕ Coffee Shops / Cafes' },
  { value: '💻 Computer', label: '💻 Computer / Laptop' },
  { value: '⚙️ Computer Parts', label: '⚙️ Computer Parts / Hardware' },
  { value: '🎵 Concerts', label: '🎵 Concerts / Music Events' },
  { value: '🏪 Convenience Store', label: '🏪 Convenience Store' },
  { value: '⛽ Convenience Store', label: '⛽ Convenience Store' },
  { value: '🏠 Council Rates', label: '🏠 Council / City Rates' },
  { value: '🏪 Counter Service', label: '🏪 Counter Service' },
  { value: '🏛️ Court', label: '🏛️ Court / Legal Fees' },
  { value: '🛳️ Cruise', label: '🛳️ Cruise' },
  { value: '💰 Debt Repayment', label: '💰 Debt Repayment' },
  { value: '🎈 Decorations', label: '🎈 Decorations / Party Supplies' },
  { value: '🦷 Dentist', label: '🦷 Dentist' },
  { value: '🏬 Department Store', label: '🏬 Department Store' },
  { value: '🍦 Dessert', label: '🍦 Dessert / Ice Cream' },
  { value: '🏪 Discount Store', label: '🏪 Discount Store (Kmart/Target)' },
  { value: '👨‍⚕️ Doctor', label: '👨‍⚕️ Doctor / GP Visit' },
  { value: '🐕 Dog Training', label: '🐕 Dog Training' },
  { value: '💸 Donation', label: '💸 Donation / Charity' },
  { value: '🍽️ Dine Out', label: '🍽️ Dine Out / Restaurant' },
  { value: '💹 Dividends', label: '💹 Dividends' },
  { value: '📊 Domain Hosting', label: '📊 Domain / Web Hosting' },
  { value: '🛍️ DoorDash', label: '🛍️ DoorDash / Food Delivery' },
  { value: '📚 Education', label: '📚 Education / School' },
  { value: '⚡ Electrician', label: '⚡ Electrician' },
  { value: '🔌 Electricity', label: '🔌 Electricity' },
  { value: '📱 Electronics', label: '📱 Electronics / Gadgets' },
  { value: '💼 Employee', label: '💼 Employee Wages' },
  { value: '🏢 Employment', label: '🏢 Employment Agency' },
  { value: '⚡ EV Charging', label: '⚡ EV Charging' },
  { value: '🎭 Events', label: '🎭 Events / Concerts / Shows' },
  { value: '🏦 Exchange Fee', label: '🏦 Exchange / Transfer Fee' },
  { value: '💰 Extra Payment', label: '💰 Extra Payment' },
  { value: '📁 File Storage', label: '📁 File Storage / Backup' },
  { value: '📋 Fines', label: '📋 Fines / Penalties' },
  { value: '💐 Flowers', label: '💐 Flowers / Plants' },
  { value: '🍽️ Food Delivery', label: '🍽️ Food Delivery (UberEats/DoorDash)' },
  { value: '💵 Freelance', label: '💵 Freelance / Contract Income' },
  { value: '⛽ Fuel', label: '⛽ Fuel / Petrol' },
  { value: '🛋️ Furniture', label: '🛋️ Furniture' },
  { value: '🎮 Game Pass', label: '🎮 Game Subscription' },
  { value: '🎮 Gaming', label: '🎮 Gaming / Video Games' },
  { value: '🌿 Gardening', label: '🌿 Gardening / Landscaping' },
  { value: '🔥 Gas', label: '🔥 Gas / Heating' },
  { value: '🎁 Gift Card', label: '🎁 Gift Card Purchase' },
  { value: '🎁 Gift Given', label: '🎁 Gift Given / Presents' },
  { value: '🎁 Gift Received', label: '🎁 Gift Received' },
  { value: '💍 Gifts', label: '💍 Gifts / Wedding' },
  { value: '✨ Gifts', label: '✨ Gifts / Presents' },
  { value: '🏌️ Golf', label: '🏌️ Golf' },
  { value: '💼 Government', label: '💼 Government Grant' },
  { value: '🛒 Groceries', label: '🛒 Groceries / Supermarket' },
  { value: '🏋️ Gym', label: '🏋️ Gym / Fitness Center' },
  { value: '💇 Haircut', label: '💇 Haircut / Barber' },
  { value: '🔨 Handyman', label: '🔨 Handyman' },
  { value: '🛠️ Hardware', label: '🛠️ Hardware / Tools' },
  { value: '🎧 Headphones', label: '🎧 Headphones / Audio' },
  { value: '🏥 Health Insurance', label: '🏥 Health Insurance Premium' },
  { value: '🏥 Health Savings', label: '🏥 Health Savings Account' },
  { value: '⚡ Heating', label: '⚡ Heating / Gas' },
  { value: '👜 High Fashion', label: '👜 High Fashion / Designer' },
  { value: '🎲 Hobbies', label: '🎲 Hobbies / Crafts' },
  { value: '🏠 Home Decor', label: '🏠 Home Decor / Furnishings' },
  { value: '🏠 Home Improvement', label: '🏠 Home Improvement / Renovation' },
  { value: '🏡 Home Insurance', label: '🏡 Home / Contents Insurance' },
  { value: '🔧 Home Maintenance', label: '🔧 Home Maintenance / Repairs' },
  { value: '🏠 Home Security', label: '🏠 Home Security System' },
  { value: '🏥 Hospital', label: '🏥 Hospital / Medical Procedure' },
  { value: '🏨 Hotels', label: '🏨 Hotels / Resorts' },
  { value: '🔑 House Cleaning', label: '🔑 House Cleaning Service' },
  { value: '💳 Income', label: '💳 Income / Revenue' },
  { value: '💰 Income Protection', label: '💰 Income Protection Insurance' },
  { value: '💰 Income Tax', label: '💰 Income Tax Payment' },
  { value: '💸 Inheritance', label: '💸 Inheritance' },
  { value: '🏦 Instore', label: '🏦 Instore Purchase' },
  { value: '🛡️ Insurance', label: '🛡️ Insurance (General)' },
  { value: '💸 Interest', label: '💸 Interest Charge' },
  { value: '💵 Interest Earned', label: '💵 Interest Earned' },
  { value: '📡 Internet', label: '📡 Internet / Broadband' },
  { value: '💰 Investment', label: '💰 Investment / Trading' },
  { value: '💍 Jewelry', label: '💍 Jewelry / Accessories' },
  { value: '💳 Late Fee', label: '💳 Late Payment Fee' },
  { value: '⚖️ Legal Fees', label: '⚖️ Legal / Attorney Fees' },
  { value: '🛡️ Life Insurance', label: '🛡️ Life Insurance' },
  { value: '🍺 Liquor', label: '🍺 Liquor / Wine' },
  { value: '💰 Loan', label: '💰 Loan Repayment' },
  { value: '💰 Loan Received', label: '💰 Loan Received' },
  { value: '🔒 Locksmith', label: '🔒 Locksmith' },
  { value: '📦 Luggage', label: '📦 Luggage / Bags' },
  { value: '🛍️ Luxury Goods', label: '🛍️ Luxury Goods' },
  { value: '📊 Magazine', label: '📊 Magazine / News Subscription' },
  { value: '🔧 Maintenance', label: '🔧 Maintenance / Repairs' },
  { value: '👨‍💼 Management Fee', label: '👨‍💼 Management Fee' },
  { value: '📊 Marketing', label: '📊 Marketing / Advertising' },
  { value: '🥊 Martial Arts', label: '🥊 Martial Arts / Boxing' },
  { value: '🧘 Massage', label: '🧘 Massage / Spa' },
  { value: '🍽️ Meal Prep', label: '🍽️ Meal Prep / Subscription' },
  { value: '🩺 Medical', label: '🩺 Medical / Health' },
  { value: '💊 Medication', label: '💊 Medication / Prescriptions' },
  { value: '🧘 Mental Health', label: '🧘 Mental Health / Therapy' },
  { value: '🔧 Minor Repairs', label: '🔧 Minor Home Repairs' },
  { value: '📱 Mobile Data', label: '📱 Mobile Data / Plan' },
  { value: '🏍️ Motorcycle', label: '🏍️ Motorcycle Expenses' },
  { value: '💰 Mortgage', label: '💰 Mortgage Payment' },
  { value: '🎬 Movies', label: '🎬 Movies / Cinema' },
  { value: '📦 Moving', label: '📦 Moving / Removalist' },
  { value: '🏛️ Museum', label: '🏛️ Museum / Art Gallery' },
  { value: '🎵 Music', label: '🎵 Music / Streaming' },
  { value: '💅 Nail Salon', label: '💅 Nail Salon' },
  { value: '📰 News', label: '📰 News Subscription' },
  { value: '💸 NSF Fee', label: '💸 NSF / Overdraft Fee' },
  { value: '🥗 Nutritionist', label: '🥗 Nutritionist / Dietitian' },
  { value: '🏢 Office Rent', label: '🏢 Office Rent' },
  { value: '📝 Office Supplies', label: '📝 Office Supplies' },
  { value: '👓 Optometrist', label: '👓 Optometrist / Glasses' },
  { value: '💵 Overtime', label: '💵 Overtime Pay' },
  { value: '🅿️ Parking', label: '🅿️ Parking Fees' },
  { value: '🎉 Party', label: '🎉 Party / Celebration' },
  { value: '🛂 Passport', label: '🛂 Passport / Visa' },
  { value: '💰 Payment', label: '💰 Payment Received' },
  { value: '🐜 Pest Control', label: '🐜 Pest Control' },
  { value: '🐕 Pet Food', label: '🐕 Pet Food / Supplies' },
  { value: '🐶 Pet Grooming', label: '🐶 Pet Grooming' },
  { value: '🐶 Pet Insurance', label: '🐶 Pet Insurance' },
  { value: '🐱 Pet Sitting', label: '🐱 Pet Sitting / Boarding' },
  { value: '🐾 Pets', label: '🐾 Pets / Animals' },
  { value: '💊 Pharmacy', label: '💊 Pharmacy / Medications' },
  { value: '📞 Phone', label: '📞 Home Phone' },
  { value: '📸 Photography', label: '📸 Photography' },
  { value: '🩹 Physiotherapy', label: '🩹 Physiotherapy / Chiropractor' },
  { value: '🧘 Pilates', label: '🧘 Pilates / Yoga' },
  { value: '🍕 Pizza', label: '🍕 Pizza / Takeaway' },
  { value: '🔌 Plumber', label: '🔌 Plumber' },
  { value: '🏊 Pool Maintenance', label: '🏊 Pool Maintenance' },
  { value: '💻 Printer', label: '💻 Printer / Scanner' },
  { value: '💼 Professional Fees', label: '💼 Professional Fees (Legal/Accounting)' },
  { value: '🎓 Professional', label: '🎓 Professional Development' },
  { value: '💰 Property Tax', label: '💰 Property / Land Tax' },
  { value: '🚌 Public Transport', label: '🚌 Public Transport (Train/Bus/Tram)' },
  { value: '🌞 Renewable Energy', label: '🌞 Renewable / Solar Energy' },
  { value: '💰 Rent', label: '💰 Rent Payment' },
  { value: '💸 Rent Received', label: '💸 Rent Received' },
  { value: '📡 Router', label: '📡 Router / Networking' },
  { value: '💵 Salary', label: '💵 Salary / Wages' },
  { value: '🏦 Savings', label: '🏦 Savings Transfer' },
  { value: '📚 School Fees', label: '📚 School Fees (K-12)' },
  { value: '✏️ School Supplies', label: '✏️ School / Office Supplies' },
  { value: '🛒 Self Care', label: '🛒 Self Care' },
  { value: '📦 Shipping', label: '📦 Shipping / Postage' },
  { value: '👟 Shoes', label: '👟 Shoes / Footwear' },
  { value: '🛒 Shopping', label: '🛒 Shopping (General)' },
  { value: '🏪 Shopping', label: '🏪 Shopping / Retail' },
  { value: '🏪 Side Hustle', label: '🏪 Side Hustle Income' },
  { value: '🧴 Skincare', label: '🧴 Skincare Products' },
  { value: '⌚ Smartwatch', label: '⌚ Smartwatch / Wearable' },
  { value: '☕ Snacks', label: '☕ Snacks / Coffee' },
  { value: '💻 Software', label: '💻 Software / SaaS' },
  { value: '💵 Solar Energy', label: '💵 Solar / Green Energy' },
  { value: '💳 Spending', label: '💳 Spending / Transaction' },
  { value: '💸 Sponsorship', label: '💸 Sponsorship' },
  { value: '⚽ Sports', label: '⚽ Sports / Recreation' },
  { value: '💲 Stamps', label: '💲 Stamps / Postage' },
  { value: '📈 Stocks', label: '📈 Stocks / Shares' },
  { value: '🏢 Strata Fees', label: '🏢 Strata / Body Corporate' },
  { value: '📺 Streaming', label: '📺 Streaming (Netflix/Disney+/Prime)' },
  { value: '🎓 Student Loan', label: '🎓 Student Loan Payment' },
  { value: '📱 Subscriptions', label: '📱 Subscriptions (Monthly)' },
  { value: '🕶️ Sunglasses', label: '🕶️ Sunglasses / Eyewear' },
  { value: '💰 Superannuation', label: '💰 Superannuation / Retirement' },
  { value: '🎾 Tennis', label: '🎾 Tennis' },
  { value: '📋 The Club', label: '📋 Club / Membership Dues' },
  { value: '🎭 Theatre', label: '🎭 Theatre / Broadway' },
  { value: '💰 Tip', label: '💰 Tip / Gratuity' },
  { value: '🛞 Tyres', label: '🛞 Tyres / Wheels' },
  { value: '🛒 Uncategorized', label: '🛒 Uncategorized' },
  { value: '🏫 University', label: '🏫 University / College Tuition' },
  { value: '🛍️ UPS Store', label: '🛍️ UPS / Post Office' },
  { value: '💡 Utilities', label: '💡 Utilities (Electricity, Water, Gas)' },
  { value: '🏖️ Vacation', label: '🏖️ Vacation / Holiday Package' },
  { value: '🩺 Vet', label: '🩺 Vet / Pet Medical' },
  { value: '🍷 Wine', label: '🍷 Wine / Spirits' },
  { value: '🧘 Yoga', label: '🧘 Yoga / Pilates' },
  
  // Default
  { value: '❓ Uncategorized', label: '❓ Uncategorized' }
];

const TransactionsTable: React.FC<Props> = ({
  jobId,
  transactions,
  onSelectionChange,
  onDataChanged
}) => {
  const theme = useTheme();
  const { mode } = useThemeMode();
  const [rows, setRows] = useState<DataverseTransaction[]>(transactions);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const [orderBy, setOrderBy] = useState<keyof DataverseTransaction>("ppp_transactiondate");
  const [order, setOrder] = useState<Order>("desc");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">("success");

  useEffect(() => {
    setRows(transactions);
  }, [transactions]);

  useEffect(() => {
    onSelectionChange?.(selected);
  }, [selected, onSelectionChange]);

  const calculateTotals = () => {
  let debit = 0;
  let credit = 0;
  rows.forEach((r) => {
    const amount = toSafeNumber(r.ppp_amountsigned);
    if (amount < 0) {
      debit += Math.abs(amount);
    } else {
      credit += amount;
    }
  });
  // Round to 2 decimal places to avoid floating point issues
  return { 
    debit: Math.round(debit * 100) / 100, 
    credit: Math.round(credit * 100) / 100 
  };
};

const totals = calculateTotals();

// This is not a true variance. It is the statement net movement.
// True variance requires opening balance and closing balance reconciliation.
const netMovement = Math.round((totals.credit - totals.debit) * 100) / 100;
const hasNetMovement = Math.abs(netMovement) >= 0.009;

  const rowsForDisplay = [...rows];

const sortedRows = [...rowsForDisplay].sort((a: any, b: any) => {
  const valueA = a[orderBy];
  const valueB = b[orderBy];

  if (valueA == null && valueB == null) return 0;
  if (valueA == null) return order === "asc" ? -1 : 1;
  if (valueB == null) return order === "asc" ? 1 : -1;

  if (valueA < valueB) return order === "asc" ? -1 : 1;
  if (valueA > valueB) return order === "asc" ? 1 : -1;

  return 0;
});

const pagedRows = sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const toggleSelect = (id: string) => {
    setSelected((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const handleChange = (id: string, field: keyof DataverseTransaction, value: any) => {
    setRows((prev) =>
      prev.map((row) => (row.ppp_sf_transactionid === id ? { ...row, [field]: value } : row))
    );
  };

  const handleSave = async (row: DataverseTransaction) => {
  // VALIDATION - Check description
  if (!row.ppp_description || row.ppp_description.trim() === "") {
    setSnackbarMessage("❌ Description is required. Please add a transaction description.");
    setSnackbarSeverity("error");
    setSnackbarOpen(true);
    return;
  }
  
  // VALIDATION - Check amount is not zero
  const amount = toSafeNumber(row.ppp_amountsigned);
  if (amount === 0) {
    setSnackbarMessage("❌ Amount cannot be zero. Please enter an amount greater than or less than 0.");
    setSnackbarSeverity("error");
    setSnackbarOpen(true);
    return;
  }
  
  setSavingId(row.ppp_sf_transactionid);
  try {
    if (row.isManual) {
      const response = await apiService.createTransaction({
        ppp_sf_job: jobId,
        ppp_description: row.ppp_description,
        ppp_amountsigned: toSafeNumber(row.ppp_amountsigned),
        ppp_transactiondate: row.ppp_transactiondate,
        ppp_category: row.ppp_category
      });
      setRows((prev) =>
        prev.map((r) =>
          r.ppp_sf_transactionid === row.ppp_sf_transactionid
            ? { ...r, ppp_sf_transactionid: response.id, isManual: false }
            : r
        )
      );
    } else {
      await apiService.updateTransaction(row.ppp_sf_transactionid, {
        ppp_description: row.ppp_description,
        ppp_amountsigned: toSafeNumber(row.ppp_amountsigned),
        ppp_category: row.ppp_category
      });
      setRows((prev) =>
        prev.map((r) =>
          r.ppp_sf_transactionid === row.ppp_sf_transactionid
            ? { ...r, ...row, ppp_amountsigned: toSafeNumber(row.ppp_amountsigned) }
            : r
        )
      );
    }
    setEditingId(null);
    setSnackbarMessage("Transaction saved successfully");
    setSnackbarSeverity("success");
    setSnackbarOpen(true);
    onDataChanged?.();
  } catch (error: any) {
    console.error("Save error:", error);
    setSnackbarMessage(error.message || "Failed to save transaction");
    setSnackbarSeverity("error");
    setSnackbarOpen(true);
  } finally {
    setSavingId(null);
  }
};

  const handleAddTransaction = () => {
  const id = "manual-" + Date.now();
  const newRow: DataverseTransaction = {
    ppp_sf_transactionid: id,
    ppp_sequenceno: rows.length + 1,
    ppp_description: "", // Empty description - will trigger validation
    ppp_amountsigned: 0, // Zero amount - will trigger validation
    ppp_transactiondate: new Date().toISOString().split("T")[0],
    ppp_category: "❓ Uncategorized",
    isManual: true,
    category: undefined
  };
  setRows(prev => [newRow, ...prev]);
  setEditingId(id);
  setTimeout(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, 100);
};

  const deleteTransaction = async (id: string) => {
    try {
      setDeletingId(id);
      const success = await apiService.deleteTransaction(id);
      if (success) {
        setRows((prev) => prev.filter((r) => r.ppp_sf_transactionid !== id));
        setSnackbarMessage("Transaction deleted successfully");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
        onDataChanged?.();
      } else {
        throw new Error("Delete failed");
      }
    } catch (error) {
      console.error("Delete failed:", error);
      setSnackbarMessage("Failed to delete transaction");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setDeletingId(null);
    }
  };
// Render category cell (editable only in edit mode) - WITH SEARCH & PRESERVES EXISTING CATEGORY

const renderCategoryCell = (row: DataverseTransaction, isEditing: boolean) => {
  if (isEditing) {
    // Get the current category value - check multiple possible field names
    let currentCategory = row.ppp_category || row.category || "❓ Uncategorized";
    
    // Ensure it's a string and trim
    currentCategory = String(currentCategory).trim();
    
    // If empty or null, set to Uncategorized
    if (!currentCategory || currentCategory === "") {
      currentCategory = "❓ Uncategorized";
    }
    
    // Find the matching option object
    const selectedOption = CATEGORY_OPTIONS.find(opt => opt.value === currentCategory);
    
    // If not found, try to find by label (for backward compatibility)
    const fallbackOption = !selectedOption 
      ? CATEGORY_OPTIONS.find(opt => opt.label === currentCategory)
      : null;
    
    // Use found option, or fallback to Uncategorized
    const finalOption = selectedOption || fallbackOption || CATEGORY_OPTIONS.find(opt => opt.value === "❓ Uncategorized");
    
    return (
      <Autocomplete
        size="small"
        options={CATEGORY_OPTIONS}
        getOptionLabel={(option) => option.label}
        value={finalOption}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        onChange={(event, newValue) => {
          const newCategory = newValue?.value || "❓ Uncategorized";
          handleChange(row.ppp_sf_transactionid, "ppp_category", newCategory);
        }}
        renderInput={(params) => (
          <MuiTextField
            {...params}
            placeholder="Search category..."
            size="small"
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <>
                  <SearchIcon sx={{ color: 'text.secondary', mr: 0.5, fontSize: 18 }} />
                  {params.InputProps.startAdornment}
                </>
              ),
            }}
            sx={{
              minWidth: 250,
              '& .MuiOutlinedInput-root': {
                color: mode === 'light' ? '#0A1E3C' : '#E5E5E5',
                '& fieldset': {
                  borderColor: mode === 'light' ? 'rgba(10, 30, 60, 0.23)' : 'rgba(255, 255, 255, 0.23)',
                },
              },
            }}
          />
        )}
        sx={{
          '& .MuiAutocomplete-popupIndicator': {
            color: mode === 'light' ? '#0A1E3C' : '#E5E5E5',
          },
        }}
      />
    );
  }
  
  // Non-edit mode - show category as plain text
  // Check multiple possible field names
  const displayCategory = row.ppp_category || row.category || "❓ Uncategorized";
  
  return (
    <Typography variant="body2" sx={{ color: mode === 'light' ? '#0A1E3C' : '#E5E5E5' }}>
      {displayCategory}
    </Typography>
  );
};

  return (
    <Paper
      sx={{
        borderRadius: 3,
        overflow: "hidden",
        border: `1px solid ${mode === "light" ? "rgba(10, 30, 60, 0.08)" : "rgba(255, 255, 255, 0.08)"}`,
        bgcolor: mode === "light" ? "#FFFFFF" : "#1E1E1E"
      }}
    >
      <Box
        sx={{
          p: 2,
          display: "flex",
          gap: 3,
          alignItems: "center",
          flexWrap: "wrap",
          borderBottom: `1px solid ${mode === "light" ? "rgba(10, 30, 60, 0.08)" : "rgba(255, 255, 255, 0.08)"}`,
          bgcolor: mode === "light" ? alpha(theme.palette.primary.main, 0.02) : alpha(theme.palette.primary.main, 0.05)
        }}
      >
        <Paper
          sx={{
            p: 1.5,
            borderRadius: 2,
            minWidth: 150,
            background: mode === "light" ? alpha(theme.palette.error.main, 0.05) : alpha(theme.palette.error.main, 0.15),
            border: `1px solid ${mode === "light" ? alpha(theme.palette.error.main, 0.2) : alpha(theme.palette.error.main, 0.3)}`
          }}
        >
          <Typography variant="caption" color="text.secondary">Total Debit</Typography>
          <Typography variant="h6" fontWeight={800} sx={{ color: mode === "light" ? theme.palette.error.main : theme.palette.error.light }}>
            ${formatMoney(totals.debit)}
          </Typography>
        </Paper>

        <Paper
          sx={{
            p: 1.5,
            borderRadius: 2,
            minWidth: 150,
            background: mode === "light" ? alpha(theme.palette.success.main, 0.05) : alpha(theme.palette.success.main, 0.15),
            border: `1px solid ${mode === "light" ? alpha(theme.palette.success.main, 0.2) : alpha(theme.palette.success.main, 0.3)}`
          }}
        >
          <Typography variant="caption" color="text.secondary">Total Credit</Typography>
          <Typography variant="h6" fontWeight={800} sx={{ color: mode === "light" ? theme.palette.success.main : theme.palette.success.light }}>
            ${formatMoney(totals.credit)}
          </Typography>
        </Paper>

{/* Variance Detector with Animation */}
{/* Variance Detector with Animation - Same style as Total Debit/Credit */}
{/* Variance Detector with Animation - AMBER VARIANCE BOX */}
<Tooltip 
  title="Net movement shows the overall change in the account based on extracted transactions: total credits minus total debits. It is not an error or variance."
  placement="top"
>
  <Paper
    sx={{
      p: 1.5,
      borderRadius: 2,
      minWidth: 150,
      background: mode === "light"
        ? alpha(theme.palette.warning.main, 0.08)
        : alpha(theme.palette.warning.main, 0.18),
      border: `1px solid ${mode === "light"
        ? alpha(theme.palette.warning.main, 0.35)
        : alpha(theme.palette.warning.main, 0.45)}`,
      transition: "all 0.3s ease",
animation: hasNetMovement ? `${pulseAnimation} 1.8s ease-in-out infinite` : "none",
"&:hover": hasNetMovement
  ? {
      transform: "scale(1.02)",
    }
  : {},
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
      <Typography variant="caption" color="text.secondary">
        NET MOVEMENT
      </Typography>
    </Box>

    <Typography
      variant="h6"
      fontWeight={800}
      sx={{
        color: netMovement < 0
          ? theme.palette.error.main
          : theme.palette.success.main
      }}
    >
      ${formatMoney(Math.abs(netMovement))}
    </Typography>

    <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.5 }}>
      {netMovement < 0 ? "More debits than credits" : "More credits than debits"}
    </Typography>
  </Paper>
</Tooltip>



        <Box sx={{ flexGrow: 1 }} />
       <Chip 
  label={hasNetMovement ? "Net Movement Calculated" : "No Net Movement"} 
  color={hasNetMovement ? "warning" : "success"}
  variant="filled"
  sx={{ 
    fontWeight: 700,
    fontSize: "0.85rem",
    py: 2
  }} 
/>
        <Button
          variant="contained"
          onClick={handleAddTransaction}
          sx={{
            background: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.light} 100%)`,
            color: "#0A1E3C",
            fontWeight: 600,
            "&:hover": {
              background: `linear-gradient(135deg, ${theme.palette.secondary.light} 0%, ${theme.palette.secondary.main} 100%)`
            }
          }}
        >
          Add Transaction
        </Button>
      </Box>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: mode === "light" ? "rgba(10, 30, 60, 0.02)" : "rgba(255, 255, 255, 0.02)" }}>
              <TableCell padding="checkbox" />
              <TableCell>
                <TableSortLabel
                  active={orderBy === "ppp_transactiondate"}
                  direction={order}
                  onClick={() => setOrderBy("ppp_transactiondate")}
                >
                  Date
                </TableSortLabel>
              </TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Category</TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={orderBy === "ppp_amountsigned"}
                  direction={order}
                  onClick={() => setOrderBy("ppp_amountsigned")}
                >
                  Amount
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">
  <Tooltip title="Running balance after this transaction">
    <span>Balance</span>
  </Tooltip>
</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {pagedRows.map((row: any) => {
              const isEditing = editingId === row.ppp_sf_transactionid;
              const isSaving = savingId === row.ppp_sf_transactionid;
              const isDeleting = deletingId === row.ppp_sf_transactionid;
              const amount = toSafeNumber(row.ppp_amountsigned);

              return (
                <TableRow
                  key={row.ppp_sf_transactionid}
                  sx={{
                    backgroundColor: row.isManual ? (mode === "light" ? "#fff9e6" : alpha(theme.palette.warning.main, 0.1)) : "inherit",
                    "&:hover": {
                      bgcolor: mode === "light" ? alpha(theme.palette.secondary.main, 0.02) : alpha(theme.palette.secondary.main, 0.05)
                    }
                  }}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selected.includes(row.ppp_sf_transactionid)}
                      onChange={() => toggleSelect(row.ppp_sf_transactionid)}
                      sx={{
                        color: mode === "light" ? "#0A1E3C" : "#E5E5E5",
                        "&.Mui-checked": { color: theme.palette.secondary.main }
                      }}
                    />
                  </TableCell>

                  <TableCell>
                    {isEditing ? (
                      <TextField
                        type="date"
                        value={row.ppp_transactiondate?.substring(0, 10) || ""}
                        onChange={(e) => handleChange(row.ppp_sf_transactionid, "ppp_transactiondate", e.target.value)}
                        size="small"
                        sx={{
                          input: { color: mode === "light" ? "#0A1E3C" : "#E5E5E5" },
                          "& .MuiOutlinedInput-root": {
                            "& fieldset": {
                              borderColor: mode === "light" ? "rgba(10, 30, 60, 0.23)" : "rgba(255, 255, 255, 0.23)"
                            }
                          }
                        }}
                      />
                    ) : row.ppp_transactiondate ? (
                      new Date(row.ppp_transactiondate).toLocaleDateString("en-AU")
                    ) : (
                      ""
                    )}
                  </TableCell>

                  <TableCell>
                    {isEditing ? (
                      <TextField
                        value={row.ppp_description}
                        onChange={(e) => handleChange(row.ppp_sf_transactionid, "ppp_description", e.target.value)}
                        size="small"
                        fullWidth
                        sx={{
                          input: { color: mode === "light" ? "#0A1E3C" : "#E5E5E5" },
                          "& .MuiOutlinedInput-root": {
                            "& fieldset": {
                              borderColor: mode === "light" ? "rgba(10, 30, 60, 0.23)" : "rgba(255, 255, 255, 0.23)"
                            }
                          }
                        }}
                      />
                    ) : (
                      <Typography sx={{ color: mode === "light" ? "#0A1E3C" : "#E5E5E5" }}>
                        {row.ppp_description}
                      </Typography>
                    )}
                  </TableCell>

                  <TableCell>
                    {renderCategoryCell(row, isEditing)}
                  </TableCell>

                  <TableCell align="right">
                    {isEditing ? (
                      <TextField
                        value={row.ppp_amountsigned ?? 0}
                        type="number"
                        onChange={(e) => handleChange(row.ppp_sf_transactionid, "ppp_amountsigned", parseFloat(e.target.value))}
                        size="small"
                        inputProps={{ step: "0.01" }}
                        sx={{
                          width: 120,
                          input: { color: mode === "light" ? "#0A1E3C" : "#E5E5E5" },
                          "& .MuiOutlinedInput-root": {
                            "& fieldset": {
                              borderColor: mode === "light" ? "rgba(10, 30, 60, 0.23)" : "rgba(255, 255, 255, 0.23)"
                            }
                          }
                        }}
                      />
                    ) : (
                      <Typography
                        sx={{
                          color: amount < 0
                            ? (mode === "light" ? theme.palette.error.main : theme.palette.error.light)
                            : (mode === "light" ? theme.palette.success.main : theme.palette.success.light),
                          fontWeight: 600
                        }}
                      >
                        {formatMoney(amount)}
                      </Typography>
                    )}
                  </TableCell>

                  <TableCell align="right">
  <Typography sx={{ color: mode === "light" ? "#0A1E3C" : "#E5E5E5" }}>
    {formatMoney(
      row.balance ??
      row.ppp_balance ??
      row.ppp_runningbalance ??
      row.ppp_netmovement
    ) || "—"}
  </Typography>
</TableCell>

                  <TableCell align="center">
  {isSaving ? (
    <CircularProgress size={18} sx={{ color: theme.palette.secondary.main }} />
  ) : isEditing ? (
    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
      <IconButton 
        onClick={() => handleSave(row)} 
        size="small"
        sx={{ 
          color: theme.palette.success.main,
          '&:hover': { bgcolor: alpha(theme.palette.success.main, 0.1) }
        }}
      >
        <SaveIcon />
      </IconButton>
      <IconButton 
        onClick={() => {
          // Reset the row to original data
          const originalRow = transactions.find(t => t.ppp_sf_transactionid === row.ppp_sf_transactionid);
          if (originalRow) {
            setRows(prev => prev.map(r => 
              r.ppp_sf_transactionid === row.ppp_sf_transactionid ? originalRow : r
            ));
          }
          setEditingId(null);
        }} 
        size="small"
        sx={{ 
          color: theme.palette.error.main,
          '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.1) }
        }}
      >
        <CancelIcon />
      </IconButton>
    </Box>
  ) : (
    <>
      <IconButton
        onClick={() => setEditingId(row.ppp_sf_transactionid)}
        sx={{
          color: mode === "light" ? "#0A1E3C" : "#E5E5E5",
          "&:hover": { color: theme.palette.secondary.main }
        }}
      >
        <EditIcon />
      </IconButton>
      {isDeleting ? (
        <CircularProgress size={18} sx={{ color: theme.palette.error.main }} />
      ) : (
        <IconButton
          onClick={() => deleteTransaction(row.ppp_sf_transactionid)}
          sx={{
            color: theme.palette.error.main,
            "&:hover": { bgcolor: alpha(theme.palette.error.main, 0.1) }
          }}
        >
          <DeleteIcon />
        </IconButton>
      )}
    </>
  )}
</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={rows.length}
        page={page}
        onPageChange={(_, p) => setPage(p)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[10, 50, 100, 200]}
        sx={{
          color: mode === "light" ? "#0A1E3C" : "#E5E5E5",
          borderTop: `1px solid ${mode === "light" ? "rgba(10, 30, 60, 0.08)" : "rgba(255, 255, 255, 0.08)"}`
        }}
      />

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: "100%", borderRadius: 2 }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default TransactionsTable;